import { Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { BreadcrumbSchema } from './BreadcrumbSchema';
import { TopicalClusters } from './TopicalClusters';
import { ArrowRight, Heart } from 'lucide-react';

interface RelatedLink {
  label: string;
  href: string;
  hint?: string;
}

interface EditorialPageLayoutProps {
  title: string;
  metaDescription: string;
  path: string;
  eyebrow: string;
  h1: string;
  lede: string;
  children: React.ReactNode;
  related?: RelatedLink[];
  closingLine?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** Optional Article JSON-LD for crawlers */
  articleSchema?: { headline: string; description: string; datePublished?: string };
  /** Absolute or root-relative path to a unique 1200x630 OG image for this page. */
  ogImage?: string;
}

/**
 * Premium minimalist editorial layout for emotional authority pages.
 * Apple-like quiet typography, generous whitespace, subtle entrance animations
 * via Tailwind utilities only — no extra runtime deps, respects reduced-motion.
 */
export function EditorialPageLayout({
  title,
  metaDescription,
  path,
  eyebrow,
  h1,
  lede,
  children,
  related = [],
  closingLine = "Celebrate people while they're alive.",
  ctaLabel = 'Start saying it now',
  ctaHref = '/signup',
  articleSchema,
  ogImage,
}: EditorialPageLayoutProps) {
  const ogImageUrl = ogImage
    ? (ogImage.startsWith('http') ? ogImage : `https://say-it-now.app${ogImage}`)
    : undefined;

  useDocumentHead({
    title,
    description: metaDescription,
    path,
    meta: [
      { property: 'og:type', content: 'article' },
      { name: 'robots', content: 'index, follow, max-image-preview:large' },
      ...(ogImageUrl
        ? [
            { property: 'og:image', content: ogImageUrl },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:image:alt', content: h1 },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:image', content: ogImageUrl },
            { name: 'twitter:image:alt', content: h1 },
          ]
        : []),
    ],
  });

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <BreadcrumbSchema
        items={[
          { name: 'Home', path: '/' },
          { name: h1, path },
        ]}
      />
      {articleSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: articleSchema.headline,
              description: articleSchema.description,
              ...(ogImageUrl ? { image: [ogImageUrl] } : {}),
              datePublished: articleSchema.datePublished ?? '2026-05-01',
              author: { '@type': 'Organization', name: 'Say It NOW' },
              publisher: {
                '@type': 'Organization',
                name: 'Say It NOW',
                logo: {
                  '@type': 'ImageObject',
                  url: 'https://say-it-now.app/og/sayitnow-og.png',
                },
              },
              mainEntityOfPage: `https://say-it-now.app${path}`,
            }),
          }}
        />
      )}

      <Header />

      <main className="flex-1">
        {/* Hero — Apple-quiet, generous breathing room */}
        <section className="container max-w-3xl px-5 pt-16 pb-12 md:pt-28 md:pb-20">
          <p
            className="text-xs md:text-sm uppercase tracking-[0.22em] text-primary/80 mb-6 motion-safe:animate-fade-in"
            style={{ animationDelay: '0ms' }}
          >
            {eyebrow}
          </p>
          <h1
            className="font-heading text-4xl md:text-6xl leading-[1.08] tracking-tight text-foreground mb-8 motion-safe:animate-fade-in"
            style={{ animationDelay: '120ms' }}
          >
            {h1}
          </h1>
          <p
            className="text-lg md:text-2xl leading-relaxed text-muted-foreground max-w-2xl motion-safe:animate-fade-in"
            style={{ animationDelay: '240ms' }}
          >
            {lede}
          </p>
        </section>

        {/* Hairline divider */}
        <div className="container max-w-3xl px-5">
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        </div>

        {/* Editorial body */}
        <section
          className="container max-w-2xl px-5 py-14 md:py-24
            prose prose-lg md:prose-xl dark:prose-invert
            prose-headings:font-heading prose-headings:tracking-tight
            prose-h2:text-3xl md:prose-h2:text-4xl prose-h2:mt-16 prose-h2:mb-6 prose-h2:leading-tight
            prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-12 prose-h3:mb-4
            prose-p:leading-[1.75] prose-p:text-foreground/85
            prose-strong:text-foreground prose-strong:font-semibold
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-a:underline-offset-4
            prose-blockquote:border-l-2 prose-blockquote:border-primary prose-blockquote:not-italic
            prose-blockquote:text-foreground/90 prose-blockquote:font-normal
            motion-safe:animate-fade-in"
        >
          {children}
        </section>

        {/* Closing line + CTA — quiet, not shouty */}
        <section className="container max-w-3xl px-5 pb-16 md:pb-24">
          <div className="border-t border-border pt-12 md:pt-16 text-center">
            <p className="font-heading text-2xl md:text-4xl leading-tight text-foreground mb-8 tracking-tight">
              {closingLine}
            </p>
            <Button asChild size="lg" className="gap-2 rounded-full px-7 h-12">
              <Link to={ctaHref}>
                <Heart className="w-4 h-4" aria-hidden /> {ctaLabel}
              </Link>
            </Button>
          </div>
        </section>

        {/* Related — minimal internal linking */}
        {related.length > 0 && (
          <section className="container max-w-3xl px-5 pb-20">
            <h2 className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-5">
              Continue reading
            </h2>
            <ul className="divide-y divide-border border-y border-border">
              {related.map((r) => (
                <li key={r.href}>
                  <Link
                    to={r.href}
                    className="group flex items-center justify-between gap-4 py-5 hover:bg-accent/30 -mx-2 px-2 rounded-sm transition-colors"
                  >
                    <span>
                      <span className="block text-foreground font-medium">{r.label}</span>
                      {r.hint && (
                        <span className="block text-sm text-muted-foreground mt-1">
                          {r.hint}
                        </span>
                      )}
                    </span>
                    <ArrowRight className="w-4 h-4 text-primary shrink-0 transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Topical clusters + secondary CTAs — strengthen topical authority */}
        <TopicalClusters currentPath={path} />
      </main>

      <Footer />
    </div>
  );
}
